import type {
  AnyElement,
  ControlConfiguration,
  Source,
} from '@src/components/sidepanel/CompositionSettings/types';

/**
 * Template control normalization (PLAT-10206 standard).
 *
 * Enforces a predictable control structure for graphic-composition templates:
 *  - Every TEXT element gets its OWN controls (no font control whose
 *    `elementPaths` span multiple elements — shared font controls are split).
 *  - Per text element, controls are ordered: Text → Text Color → Font →
 *    Font Size, followed by any other controls for that element (e.g.
 *    Background Color, Opacity) in a stable order. A text element with no
 *    Font Size control gets a minimal one synthesized (`number`/`font_size`) —
 *    the element's own `font_size` in the source is the effective standard, so
 *    the synthesized control carries no default/customValue. The separate Font
 *    Weight control is dropped — the Font control's variant (a postScriptName)
 *    already encodes the weight, and that weight is re-derived on apply
 *    (applyDefaultControlValuesToSource).
 *  - The `speaker` control (if any) is emitted FIRST — the picker precedes the
 *    fields it drives (e.g. Speaker → Name → Name Font → Title → Title Font).
 *    Its bound text controls (a name tag's name / title) are normalized into
 *    their own element groups, so each field sits with its font. Bindings
 *    survive (they're keyed by control `id`, which relabel preserves).
 *  - Other non-text controls (image, shape, composition, …) are preserved as-is,
 *    in their original relative order, after the text groups.
 *  - Labels follow `{Element} {Attribute}` (element-first): a primary field
 *    (text body / image source) is just the element name; secondary attributes
 *    get a short noun (Color, Font, Font Weight, Background, Opacity).
 *
 * Pure + idempotent: running it twice yields the same result. Existing
 * `customValue`/`default`/`enabled` are preserved by matching on
 * (property + element path); labels are restandardized from the element name.
 */

// Standard ordering of a text element's controls by `property`. font_weight is
// intentionally absent — the Font control owns weight (see DROPPED_PROPERTIES).
const TEXT_PROPERTY_ORDER = [
  'text',
  'fill_color',
  'font_family',
  'font_size',
] as const;

// Controls whose property is redundant under the single-Font-control standard
// and are removed on normalization. The Font control's variant postScriptName
// fully specifies family + weight, so a separate Font Weight control is dropped.
const DROPPED_PROPERTIES = new Set(['font_weight']);

// `{Element} {Attribute}` label standard. Properties not listed (and the
// primary `text`/`source` fields) get just the bare element name.
const ATTRIBUTE_LABEL_SUFFIX: Record<string, string> = {
  fill_color: 'Color',
  font_family: 'Font',
  font_size: 'Font Size',
  background_color: 'Background',
  opacity: 'Opacity',
};
const PRIMARY_CONTENT_PROPERTIES = new Set(['text', 'source']);

/**
 * Standardized control label `{Element} {Attribute}` (element name first). The
 * element's primary field (`text`/`source`) is just the element name. Returns
 * undefined when no good label can be derived (caller keeps the existing one).
 */
export const standardControlLabel = (
  elementName: string | undefined,
  property: string,
): string | undefined => {
  const el = (elementName ?? '').trim();
  if (!el) return undefined;
  const suffix = ATTRIBUTE_LABEL_SUFFIX[property];
  if (suffix) return `${el} ${suffix}`;
  if (PRIMARY_CONTENT_PROPERTIES.has(property)) return el;
  return undefined;
};

/** A control's author-chosen label, when it has a non-blank one. */
const customLabelOf = (control: ControlConfiguration): string | undefined => {
  const custom = control.customLabel?.trim();
  return custom ? custom : undefined;
};

/** The control with its custom label applied, when it has one. */
const withCustomLabel = (
  control: ControlConfiguration,
): ControlConfiguration => {
  const custom = customLabelOf(control);
  return custom ? { ...control, label: custom } : control;
};

type ElementInfo = { path: number[]; element: AnyElement };

/** Recursively enumerate every element in a source tree with its numeric path. */
export const enumerateElements = (
  source: Pick<Source, 'elements'> | null | undefined,
  basePath: number[] = [],
): ElementInfo[] => {
  const out: ElementInfo[] = [];
  const elements = source?.elements;
  if (!Array.isArray(elements)) return out;
  elements.forEach((element, index) => {
    const path = [...basePath, index];
    out.push({ path, element });
    if (
      element &&
      typeof element === 'object' &&
      'elements' in element &&
      Array.isArray((element as { elements?: unknown }).elements)
    ) {
      out.push(...enumerateElements(element as Pick<Source, 'elements'>, path));
    }
  });
  return out;
};

const pathKey = (path: number[]): string => path.join('.');

/** A control "atom" = one control bound to exactly one element path. */
type ControlAtom = {
  control: ControlConfiguration;
  path: number[];
  /** original index, used to keep a stable order for non-text controls */
  order: number;
};

/**
 * Expand controls into per-element atoms. A shared control (multiple
 * `elementPaths`) becomes one atom per path — this is the "split" step.
 * Controls with no element paths (e.g. the speaker control) are kept as a
 * single atom with an empty path.
 */
const explodeControls = (controls: ControlConfiguration[]): ControlAtom[] => {
  const atoms: ControlAtom[] = [];
  controls.forEach((control, order) => {
    const paths = Array.isArray(control.elementPaths)
      ? control.elementPaths
      : [];
    if (paths.length <= 1) {
      atoms.push({ control, path: paths[0] ?? [], order });
      return;
    }
    // Shared control → one atom per path (preserve all other fields).
    paths.forEach(path => {
      atoms.push({
        control: { ...control, elementPaths: [path] },
        path,
        order,
      });
    });
  });
  return atoms;
};

const isTextElement = (element: AnyElement): boolean =>
  !!element && typeof element === 'object' && element.type === 'text';

/**
 * Build the standardized control list for a template.
 *
 * @param source   the composition Creatomate source (read-only)
 * @param controls the template's current controls
 */
export const normalizeTemplateControls = (
  source: Pick<Source, 'elements'> | null | undefined,
  controls: ControlConfiguration[] | null | undefined,
): ControlConfiguration[] => {
  if (!Array.isArray(controls) || controls.length === 0) return controls ?? [];

  // Split shared controls into per-element atoms, then drop redundant ones
  // (font_weight) — the Font control fully specifies the face. Filtering here
  // keeps them out of the ordered text groups AND the non-text passthrough.
  const atoms = explodeControls(controls).filter(
    a => !DROPPED_PROPERTIES.has(a.control.property),
  );

  // Index atoms by `${pathKey}|${property}` for per-element lookup. If the same
  // (path, property) appears more than once, the first wins (stable).
  const byPathProperty = new Map<string, ControlAtom>();
  for (const atom of atoms) {
    const key = `${pathKey(atom.path)}|${atom.control.property}`;
    if (!byPathProperty.has(key)) byPathProperty.set(key, atom);
  }

  const consumed = new Set<ControlAtom>();
  const result: ControlConfiguration[] = [];

  // Element name per path, for the `{Element} {Attribute}` label standard.
  const elements = enumerateElements(source);
  const nameByPath = new Map<string, string>();
  for (const { path, element } of elements) {
    const name = element?.name;
    if (typeof name === 'string' && name.trim()) {
      nameByPath.set(pathKey(path), name.trim());
    }
  }
  // A custom label (PLAT-11993) wins; otherwise `{Element} {Attribute}`.
  const relabel = (atom: ControlAtom): ControlConfiguration => {
    const custom = customLabelOf(atom.control);
    const label =
      custom ??
      standardControlLabel(
        nameByPath.get(pathKey(atom.path)),
        atom.control.property,
      );
    return label ? { ...atom.control, label } : atom.control;
  };

  // 0) Speaker control(s) first — the picker logically precedes the fields it
  //    drives (e.g. Speaker → Name → Name Font → Title → Title Font). Bound
  //    name/title text controls are normalized into their own element groups
  //    below, so each field sits with its font.
  atoms
    .filter(a => a.control.type === 'speaker')
    .sort((a, b) => a.order - b.order)
    .forEach(a => {
      consumed.add(a);
      result.push(withCustomLabel(a.control));
    });

  // 1) Emit each TEXT element's controls in the standard order.
  for (const { path, element } of elements) {
    if (!isTextElement(element)) continue;
    const pk = pathKey(path);

    // Core controls in the required order. An editable text element (one with
    // a Text control) that lacks a Font Size control gets a minimal one
    // synthesized — NumberControl already implements font_size editing.
    for (const property of TEXT_PROPERTY_ORDER) {
      const atom = byPathProperty.get(`${pk}|${property}`);
      if (atom && !consumed.has(atom)) {
        consumed.add(atom);
        result.push(relabel(atom));
      } else if (
        property === 'font_size' &&
        !atom &&
        byPathProperty.has(`${pk}|text`)
      ) {
        result.push({
          label:
            standardControlLabel(nameByPath.get(pk), 'font_size') ??
            'Font Size',
          type: 'number',
          elementPaths: [path],
          property: 'font_size',
        });
      }
    }
    // Any remaining controls for this same text element (e.g. background_color,
    // opacity) follow the core controls, in their original order.
    atoms
      .filter(
        a => !consumed.has(a) && a.path.length > 0 && pathKey(a.path) === pk,
      )
      .sort((a, b) => a.order - b.order)
      .forEach(a => {
        consumed.add(a);
        result.push(relabel(a));
      });
  }

  // 2) Append every remaining control (non-text, non-speaker — e.g. image) in
  //    its original order — nothing is dropped.
  atoms
    .filter(a => !consumed.has(a))
    .sort((a, b) => a.order - b.order)
    .forEach(a => {
      consumed.add(a);
      result.push(withCustomLabel(a.control));
    });

  return result;
};

// Creatomate auto-names a duplicated text layer "Text-<token>" (e.g. "Text-TLB",
// "Text-5DH") — the token is a random dedup suffix, not a meaningful name. Match
// the bare type word "Text" optionally followed by one such short token.
const GENERIC_TEXT_NAME = /^text(?:-[a-z0-9]{1,8})?$/i;

/**
 * Element path → a clean "Text-N" display name, for generic placeholder text
 * layers ONLY (names like "Text-TLB"). Numbered in document order. Meaningfully
 * named layers are intentionally absent from the map, so callers leave their
 * labels untouched.
 */
const buildTextPlaceholderNames = (
  source: Pick<Source, 'elements'> | null | undefined,
): Map<string, string> => {
  const names = new Map<string, string>();
  let n = 0;
  for (const { path, element } of enumerateElements(source)) {
    const name = typeof element?.name === 'string' ? element.name.trim() : '';
    if (isTextElement(element) && GENERIC_TEXT_NAME.test(name)) {
      n += 1;
      names.set(pathKey(path), `Text-${n}`);
    }
  }
  return names;
};

/**
 * Relabel ONLY the controls of generic placeholder text layers ("Text-<token>")
 * to a clean, sequential "Text-N" (body → "Text-N", color → "Text-N Color",
 * font → "Text-N Font"); every other control's label is left exactly as-is.
 * Pure and non-reordering — safe to run on already-saved controls for display,
 * so the random Creatomate dedup tokens don't leak into the UI. Control matching
 * everywhere is by property + elementPaths (never label), so this is cosmetic.
 */
export const standardizeControlLabels = (
  source: Pick<Source, 'elements'> | null | undefined,
  controls: ControlConfiguration[] | null | undefined,
): ControlConfiguration[] => {
  if (!Array.isArray(controls) || controls.length === 0) return controls ?? [];
  const placeholderNames = buildTextPlaceholderNames(source);
  if (placeholderNames.size === 0) return controls;
  return controls.map(control => {
    // An author-chosen label is never a placeholder to clean up.
    if (customLabelOf(control)) return control;
    const path = control.elementPaths?.[0];
    const displayName = path ? placeholderNames.get(pathKey(path)) : undefined;
    if (!displayName) return control;
    const label = standardControlLabel(displayName, control.property);
    return label ? { ...control, label } : control;
  });
};
